import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getStoreIconMap, IconName, IconComponent, IconMap } from 'shared/icons';
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';
import { Theme } from '@nykaa/ui-components/themes/types';
import { getStoreId } from 'shared/store/app/selectors';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import { resolveThemeColor, formatAvailableIcons } from './utils';

/**
 * Get theme-aware icon component for current store
 * - Icons selected based on themeName from remote config (light/dark)
 * - NYKAA_FASHION: All stores use same icon map based on themeName
 * - FOOT_LOCKER: Uses default light theme icons
 * - Each store gets unique colors from its own theme
 * - Colors can be overridden with color prop
 */
export const useStoreIcon = (
  iconName: IconName,
  variant: 'default' | 'filled' = 'default'
): IconComponent => {
  const storeId = useSelector(getStoreId);
  const themeName: string = useRemoteConfig('themeName') || 'light';
  const iconMap = getStoreIconMap(themeName);

  const WrappedIcon = useMemo(() => {
    const iconEntry = iconMap[iconName];

    if (!iconEntry) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          `[Icons] Icon "${iconName}" not found for store "${storeId}".`,
          '\nAvailable:',
          formatAvailableIcons(iconMap)
        );
      }
      return (() => null) as IconComponent;
    }

    const BaseIcon =
      variant === 'filled' && iconEntry.filled ? iconEntry.filled : iconEntry.default;

    const defaultColorToken =
      variant === 'filled' && iconEntry.filledColor
        ? iconEntry.filledColor
        : iconEntry.defaultColor;

    const ThemedIconWrapper: IconComponent = (props: React.ComponentProps<IconComponent>) => {
      const rest = { ...props };
      const color = rest.color;
      delete rest.color;
      const theme = useTheme() as Theme;
      const resolvedColor = color || resolveThemeColor(theme, defaultColorToken);

      return React.createElement(BaseIcon, {
        ...rest,
        color: resolvedColor,
      });
    };

    return ThemedIconWrapper;
  }, [iconMap, iconName, variant, storeId]);

  return WrappedIcon;
};

/**
 * Get entire icon map for current store
 */
export const useStoreIconMap = (): IconMap => {
  const themeName: string = useRemoteConfig('themeName') || 'light';
  return getStoreIconMap(themeName);
};
